/**
 * Universal structured logger for the group portal.
 *
 * Server: emits structured JSON to stdout (production) or pretty console (dev).
 * Client: emits to Datadog Browser Logs when initialized AND console for
 * devtools. Sensitive keys (ssn, password, token, authorization, etc.) are
 * redacted before transport.
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug'

interface LogContext {
  [key: string]: unknown
}

type BrowserLogger = {
  debug: (message: string, context?: Record<string, unknown>) => void
  info: (message: string, context?: Record<string, unknown>) => void
  warn: (message: string, context?: Record<string, unknown>) => void
  error: (message: string, context?: Record<string, unknown>) => void
} | null

let mockBrowserLogger: BrowserLogger = null

/** Test seam: inject a mock browser logger. */
export function _setMockBrowserLogger(mock: BrowserLogger): void {
  mockBrowserLogger = mock
}

const SENSITIVE_KEYS = [
  'password',
  'token',
  'accesstoken',
  'refreshtoken',
  'ssn',
  'socialsecurity',
  'creditcard',
  'authorization',
  'email',
  'phone',
  'phonenumber',
  'dob',
  'dateofbirth',
  // A member-search term can carry SSN digits a user typed; redact it by key so
  // it is never logged in clear even if upstream SSN parsing misses a variant.
  'search',
]

const FALSE_POSITIVES = ['tokentype']

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase()
  if (FALSE_POSITIVES.includes(lower)) return false
  return SENSITIVE_KEYS.some(s => lower === s || lower.includes(s))
}

function filterSensitive(
  input: Record<string, unknown>,
  visited: WeakSet<object> = new WeakSet(),
): Record<string, unknown> {
  if (visited.has(input)) {
    return { '[CIRCULAR_REFERENCE]': true }
  }
  visited.add(input)

  const out: Record<string, unknown> = { ...input }
  for (const key of Object.keys(out)) {
    const value = out[key]
    const sensitive = isSensitiveKey(key)

    if (Array.isArray(value)) {
      if (sensitive) {
        out[key] = value.map(() => '[REDACTED]')
      } else {
        out[key] = value.map(v =>
          v && typeof v === 'object'
            ? filterSensitive(v as Record<string, unknown>, visited)
            : v,
        )
      }
      continue
    }

    if (value && typeof value === 'object') {
      out[key] = filterSensitive(value as Record<string, unknown>, visited)
      continue
    }

    if (sensitive) {
      out[key] = '[REDACTED]'
    }
  }
  return out
}

/**
 * Emits Datadog's standard error attributes (`error.message`, `error.stack`,
 * `error.kind`). A flat string `error` attribute is dropped by Datadog's error
 * remapper, which leaves error logs with no visible detail.
 */
function extractErrorContext(error: unknown): Record<string, unknown> {
  if (!error) return {}
  if (error instanceof Error) {
    return {
      error: { message: error.message, stack: error.stack, kind: error.name },
    }
  }
  return { error: { message: String(error) } }
}

function getBrowserLogger(): BrowserLogger {
  if (mockBrowserLogger) return mockBrowserLogger
  if (typeof window === 'undefined') return null
  try {
    const mod = require('@datadog/browser-logs') as {
      datadogLogs?: { logger?: BrowserLogger }
    }
    return mod?.datadogLogs?.logger ?? null
  } catch {
    return null
  }
}

class Logger {
  private emitServer(level: LogLevel, message: string, ctx?: LogContext): void {
    const timestamp = new Date().toISOString()
    const safeCtx = ctx ? filterSensitive(ctx) : undefined
    const payload = { timestamp, level, message, ...(safeCtx ?? {}) }

    if (process.env.NODE_ENV === 'development') {
      const method = level === 'info' ? 'log' : level
      const prefix = `[${timestamp}] [${level.toUpperCase()}]`

      // Same rule as emitClient: message can carry caller-influenced text,
      // never let it sit in the format-string position.
      console[method]('%s %s', prefix, message, safeCtx ?? '')
      return
    }

    console.log(JSON.stringify(payload))
  }

  private emitClient(level: LogLevel, message: string, ctx?: LogContext): void {
    const safeCtx = ctx ? filterSensitive(ctx) : {}

    const browserLogger = getBrowserLogger()
    if (browserLogger) {
      try {
        browserLogger[level](message, safeCtx)
      } catch {
        // never fail callers on transport errors
      }
    }

    if (process.env.NODE_ENV === 'development') {
      // `message` can carry caller/user-influenced text. Never let it sit in
      // the format-string position (arg 0): pass a static '%s %s' template
      // instead, with the level tag and message as substitution arguments,
      // so console's util.format-style scanning never re-parses message's
      // own content as additional format directives.
      const levelTag = `[${level.toUpperCase()}]`
      if (level === 'error') {
        console.error('%s %s', levelTag, message, safeCtx)
      } else if (level === 'warn') {
        console.warn('%s %s', levelTag, message, safeCtx)
      } else if (level === 'debug') {
        console.debug('%s %s', levelTag, message, safeCtx)
      } else {
        console.log('%s %s', levelTag, message, safeCtx)
      }
    }
  }

  private emit(level: LogLevel, message: string, ctx?: LogContext): void {
    if (typeof window === 'undefined') {
      this.emitServer(level, message, ctx)
    } else {
      this.emitClient(level, message, ctx)
    }
  }

  info(message: string, context?: LogContext): void {
    this.emit('info', message, context)
  }

  warn(message: string, context?: LogContext): void {
    this.emit('warn', message, context)
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    this.emit('error', message, {
      ...(context ?? {}),
      ...extractErrorContext(error),
    })
  }

  debug(message: string, context?: LogContext): void {
    if (process.env.NODE_ENV !== 'development') return
    this.emit('debug', message, context)
  }
}

export const logger = new Logger()
